import React, { useMemo, useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { getYearEnd, rebuildRetainedProfit } from "api";
import { toast } from "react-toastify";
import { useCompany, resetCompanyCache, getCompany } from "services/companyService";
import { DateFormatFull } from "components/common/formatter";
import OtpInput from "components/common/OtpInput";
import SubmitButton from "components/common/SubmitButton";
import { LINQ_STATUS } from "CONSTANT/statusCodes";
import styles from "./YearEnd.module.scss";

const addOneYear = (date) => {
  if (!date) return null;
  const next = new Date(date);
  next.setFullYear(next.getFullYear() + 1);
  return next;
};

const YearEnd = () => {
  const { data: company } = useCompany();
  const [authDialog, setAuthDialog] = useState(null);
  const [authCode, setAuthCode] = useState("");
  const [authErrorMsg, setAuthErrorMsg] = useState("");
  const [loading, setLoading] = useState(false);

  const currentYearValue = useMemo(() => {
    if (!company?.yearStartDate) return null;
    return new Date(company.yearStartDate).getFullYear();
  }, [company?.yearStartDate]);

  const currentFinancialYear = useMemo(
    () => ({
      start: DateFormatFull(company?.yearStartDate),
      end: DateFormatFull(company?.yearEndDate),
    }),
    [company?.yearStartDate, company?.yearEndDate],
  );

  const nextYear = useMemo(
    () => ({
      start: DateFormatFull(addOneYear(company?.yearStartDate)),
      end: DateFormatFull(addOneYear(company?.yearEndDate)),
    }),
    [company?.yearStartDate, company?.yearEndDate],
  );

  const nextYearValue = currentYearValue ? currentYearValue + 1 : null;
  const currentRange = currentYearValue
    ? `${currentFinancialYear.start} – ${currentFinancialYear.end}`
    : "—";
  const nextRange = currentYearValue ? `${nextYear.start} – ${nextYear.end}` : "—";

  const openAuthDialog = (action) => {
    setAuthCode("");
    setAuthErrorMsg("");
    setAuthDialog(action);
  };

  const closeAuthDialog = () => {
    setAuthDialog(null);
    setAuthCode("");
    setAuthErrorMsg("");
  };

  const handleConfirm = async () => {
    if (authCode.length !== 4) return;

    setLoading(true);
    setAuthErrorMsg("");

    const payload = { currentYear: currentYearValue, authorisationCode: authCode };

    try {
      if (authDialog === "yearEnd") {
        const response = await getYearEnd(payload);
        if (response.status === 200) {
          toast.success("Year end process completed successfully");
          resetCompanyCache();
          await getCompany();
          closeAuthDialog();
        }
      } else if (authDialog === "retainedProfit") {
        const response = await rebuildRetainedProfit(payload);
        if (response.status === 200) {
          toast.success("Retained profit rebuilt successfully");
          closeAuthDialog();
        }
      }
    } catch (error) {
      if (error?.status === LINQ_STATUS.InvalidUserAuthCode) {
        setAuthErrorMsg(error?.data?.message || "Invalid authorisation code.");
      }
    } finally {
      setLoading(false);
    }
  };

  const dialogTitle =
    authDialog === "yearEnd" ? "Run Year End" : "Rebuild Retained Profit";

  return (
    <>
      <div className={`${styles.card} mx-auto p-4 p-md-5 rounded-3 my-5`}>
        <p className={`${styles.lead} mb-4`}>
          {currentYearValue
            ? `Close ${currentYearValue} and carry balances forward into ${nextYearValue}.`
            : "The financial year for this company isn't available yet."}
        </p>

        <div className={`${styles.grid} d-flex`}>
          <div className={`${styles.panel} flex-fill`}>
            <div className={styles.panelLabel}>Current Financial year</div>
            <div className={`${styles.range} mt-1`}>{currentRange}</div>
          </div>

          <div
            className={`${styles.arrow} d-flex align-items-center justify-content-center`}
            aria-hidden="true">
            →
          </div>

          <div className={`${styles.panel} ${styles.panelNext} flex-fill`}>
            <div className={`${styles.panelLabel} ${styles.panelLabelNext}`}>
              Next Financial year
            </div>
            <div className={`${styles.range} mt-1`}>{nextRange}</div>
          </div>
        </div>

        <div
          className={`${styles.actions} d-flex flex-wrap justify-content-between align-items-center mt-4 pt-4`}>
          <Button
            onClick={() => openAuthDialog("retainedProfit")}
            className="common-btn bgBtnSecondary border-0"
            disabled={loading || !currentYearValue}>
            Rebuild Retained Profit
          </Button>
          <Button
            onClick={() => openAuthDialog("yearEnd")}
            className="common-btn"
            disabled={loading || !currentYearValue}>
            Run Year End
          </Button>
        </div>
      </div>

      {authDialog && (
        <Dialog title={dialogTitle} onClose={closeAuthDialog} className="dialog-sm" width={400}>
          <div className="d-flex flex-column align-items-center px-4 py-5">
            <label className="mb-2 fw-bold">Authorisation Code</label>
            <OtpInput
              value={authCode}
              onChange={setAuthCode}
              length={4}
            />
            {authErrorMsg && <p className="mt-2 mb-0 text-danger">{authErrorMsg}</p>}
          </div>
          <DialogActionsBar layout="end">
            <div className="dialog-footer">
              <Button
                onClick={closeAuthDialog}
                className="common-btn bgBtnSecondary border-0 me-2"
                disabled={loading}>
                Cancel
              </Button>
              <SubmitButton
                type="button"
                onClick={handleConfirm}
                className="me-4"
                isLoading={loading}
                disabled={authCode.length !== 4}>
                Confirm
              </SubmitButton>
            </div>
          </DialogActionsBar>
        </Dialog>
      )}
    </>
  );
};

export default YearEnd;
